import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import apiService from "services/apiService";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToaster } from "contexts/toasterContext";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { customSelectStyles } from "util/selectStyles";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  UserPlus,
  RotateCcw,
  KeyRound,
  Calendar,
} from "lucide-react";
import { isValidEmail } from "util/validation";
import { handleNameInput } from "util/Util";
import { preventSpaceInputInField, removeInvalidTextCharacters } from "util/preventSpecialCharInput";

// ─── Options (reused from the employee form) ───────────────────────────────────

interface Option<T = string> {
  value: T;
  label: string;
}

const genderOptions: Option[] = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

// Same designation list as the employee edit form, with "ONBOARDING" added for
// users who have joined but are not yet assigned a role/designation.
const designationOptions: Option[] = [
  { value: "ONBOARDING", label: "Onboarding" },
  { value: "Web Developer", label: "Web Developer" },
  { value: "Java Developer", label: "Java Developer" },
  { value: "Developer", label: "Developer" },
  { value: "Windows developer", label: "Windows developer" },
  { value: "Programmer", label: "Programmer" },
  { value: "Systems Analyst", label: "Systems Analyst" },
  { value: "Software engineer", label: "Software engineer" },
  { value: "Fullstack Developer", label: "Fullstack Developer" },
  { value: "HR manager", label: "HR manager" },
  { value: "Android Developer", label: "Android Developer" },
  { value: "IOS Developer", label: "iOS Developer" },
  { value: "QA Engineer", label: "QA Engineer" },
  { value: "Test Manager", label: "Test Manager" },
  { value: "Test Engineer", label: "Test Engineer" },
  { value: "Test Analyst", label: "Test Analyst" },
  { value: "Test Automation Engineer", label: "Test Automation Engineer" },
];

// Role values match the backend (1 = Employee, 2 = Admin, 3 = Sys_Admin, 4 = Manager).
const roleOptions: Option<number>[] = [
  { value: 1, label: "Employee" },
  { value: 2, label: "Admin" },
  { value: 3, label: "Sys_Admin" },
  { value: 4, label: "Manager" },
];

// New users default to the Employee role.
const DEFAULT_ROLE = 1;

// Shared blank form state — reused by the initial config and the autofill-clearing
// resets below so the Employee role default is always preserved.
const DEFAULT_VALUES = {
  displayName: "",
  email: "",
  password: "",
  confirmPassword: "",
  socialProvider: "",
  gender: "",
  designation: "",
  doj: "",
  role: DEFAULT_ROLE,
};

// ─── Validation ───────────────────────────────────────────────────────────────

const validSchema = yup.object().shape({
  displayName: yup.string().required("User name is required"),
  email: yup
    .string()
    .required("Email is required")
    .test(
      "is-valid-email",
      "Please enter a valid email address",
      (value) => !value || isValidEmail(value)
    ),
  password: yup
    .string()
    .min(8, "Password must be between 8 and 16 characters")
    .max(16, "Password must be between 8 and 16 characters")
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9\s])[^\s]+$/,
      "Password must contain at least one digit, one lowercase letter, one uppercase letter, one special character, and no whitespace"
    )
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords do not match")
    .required("Please confirm your password"),
  gender: yup.string().required("Gender is required"),
  designation: yup.string().required("Designation is required"),
  // Date of joining is optional.
  doj: yup.string().optional(),
  role: yup.number().required("Role is required"),
  socialProvider: yup.string(),
});

// ─── Password strength helper ─────────────────────────────────────────────────

function getPasswordStrength(value: string): {
  score: number;
  label: string;
  color: string;
} {
  if (!value) return { score: 0, label: "", color: "" };
  let score = 0;
  if (value.length >= 6) score++;
  if (value.length >= 10) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[0-9]/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;

  if (score <= 1) return { score, label: "Weak", color: "bg-red-400" };
  if (score <= 3) return { score, label: "Fair", color: "bg-yellow-400" };
  return { score, label: "Strong", color: "bg-green-500" };
}

// ─── Field component ──────────────────────────────────────────────────────────

type FieldProps = {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
};

const Field = ({ label, error, required, children }: FieldProps) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && (
      <p className="flex items-center gap-1 text-xs text-red-500 mt-0.5">
        <span className="inline-block w-1 h-1 rounded-full bg-red-500 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Register = () => {
  const { addToast } = useToaster();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const methods = useForm({
    resolver: yupResolver(validSchema),
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = methods;

  const passwordValue = watch("password", "");
  const strength = getPasswordStrength(passwordValue);

  // Current select values (react-select is controlled from RHF state).
  const genderValue = watch("gender");
  const designationValue = watch("designation");
  const roleValue = watch("role");

  const confirmPassword = watch("confirmPassword");
  useEffect(() => {
    if (passwordValue && confirmPassword && passwordValue !== confirmPassword) {
      setError("confirmPassword", { type: "manual", message: "Passwords do not match" });
    } else {
      clearErrors("confirmPassword");
    }
  }, [passwordValue, confirmPassword, setError, clearErrors]);

  useEffect(() => {
    // Clear potential browser-injected values on first paint.
    reset(DEFAULT_VALUES);

    const timeout = window.setTimeout(() => {
      reset(DEFAULT_VALUES);
    }, 50);

    return () => window.clearTimeout(timeout);
  }, [reset]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        ...data,
        socialProvider: "LOCAL",
        email: data.email.trim().toLowerCase(),
      };
      // doj is optional — don't send an empty value.
      if (!payload.doj) delete payload.doj;

      await apiService.post("api/user/new", payload);
      addToast("User registered successfully", "success");
      reset(DEFAULT_VALUES);
    } catch (error) {
      addToast(error?.response?.data || "Registration failed. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => reset(DEFAULT_VALUES);

  const inputBase =
    "registration-input-field w-full pl-10 pr-4 h-10 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
  const inputNormal = "border-gray-200 dark:border-gray-600";
  const inputError = "border-red-400 focus:ring-red-400";

  return (
    <FormProvider {...methods}>
      {/* Full-height centering wrapper */}
      <div className="min-h-[calc(100vh-2px)] flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800">
        {/* Card */}
        <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

          {/* Card header stripe */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                <ShieldCheck className="text-white w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">Create Account</h1>
                <p className="text-blue-100 text-xs">Register a new user to the system</p>
              </div>
            </div>
          </div>

          {/* Form body */}
          <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4" autoComplete="off">

            {/* Decoy fields discourage browser credential autofill on registration forms */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              tabIndex={-1}
              aria-hidden="true"
              className="hidden"
            />
            <input
              type="password"
              name="current-password"
              autoComplete="current-password"
              tabIndex={-1}
              aria-hidden="true"
              className="hidden"
            />

            {/* User Name */}
            <Field label="User Name" error={errors.displayName?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <User size={15} />
                </div>
                <input
                  {...register("displayName")}
                  type="text"
                  placeholder="Enter user name"
                  className={`${inputBase} ${errors.displayName ? inputError : inputNormal}`}
                  onInput={(e) => {
                    preventSpaceInputInField(e)
                    removeInvalidTextCharacters(e);
                    handleNameInput(e, methods.setValue);
                  }}
                />
              </div>
            </Field>

            {/* Email */}
            <Field label="Email Address" error={errors.email?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Mail size={15} />
                </div>
                <input
                  {...register("email")}
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-form-type="other"
                  className={`${inputBase} ${errors.email ? inputError : inputNormal}`}
                  onInput={removeInvalidTextCharacters}
                />
              </div>
            </Field>

            {/* Password */}
            <Field label="Password" error={errors.password?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={15} />
                </div>
                <input
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  autoComplete="new-password"
                  data-form-type="other"
                  className={`${inputBase} pr-10 ${errors.password ? inputError : inputNormal}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {/* Password strength bar */}
              {passwordValue && (
                <div className="mt-1.5 space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          i <= strength.score ? strength.color : "bg-gray-200 dark:bg-gray-600"
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-[11px] font-medium ${
                    strength.score <= 1 ? "text-red-500" :
                    strength.score <= 3 ? "text-yellow-500" : "text-green-600"
                  }`}>
                    {strength.label} password
                  </p>
                </div>
              )}
            </Field>

            {/* Confirm Password */}
            <Field label="Confirm Password" error={errors.confirmPassword?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={15} />
                </div>
                <input
                  {...register("confirmPassword")}
                  type={showConfirm ? "text" : "password"}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  data-form-type="other"
                  className={`${inputBase} pr-10 ${errors.confirmPassword ? inputError : inputNormal}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>

            {/* Gender + Date of Joining */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Gender" error={errors.gender?.message as string} required>
                <Select
                  inputId="gender"
                  options={genderOptions}
                  placeholder="Select gender"
                  styles={customSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  value={genderOptions.find((o) => o.value === genderValue) || null}
                  onChange={(option) =>
                    setValue("gender", option?.value ?? "", {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                />
              </Field>

              <Field label="Date of Joining" error={errors.doj?.message as string}>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Calendar size={15} />
                  </div>
                  <input
                    {...register("doj")}
                    type="date"
                    className={`${inputBase} ${errors.doj ? inputError : inputNormal}`}
                  />
                </div>
              </Field>
            </div>

            {/* Designation + Role */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Designation" error={errors.designation?.message as string} required>
                <Select
                  inputId="designation"
                  options={designationOptions}
                  placeholder="Select designation"
                  styles={customSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  value={designationOptions.find((o) => o.value === designationValue) || null}
                  onChange={(option) =>
                    setValue("designation", option?.value ?? "", {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                />
              </Field>

              <Field label="Role" error={errors.role?.message as string} required>
                <Select
                  inputId="role"
                  options={roleOptions}
                  placeholder="Select role"
                  styles={customSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  value={roleOptions.find((o) => o.value === roleValue) || null}
                  onChange={(option) =>
                    setValue("role", option?.value ?? DEFAULT_ROLE, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                />
              </Field>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={isSubmitting || !isDirty || !isValid}
                className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors shadow-sm"
              >
                <UserPlus size={15} />
                {isSubmitting ? "Registering…" : "Register"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <RotateCcw size={14} />
                Reset
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/change-password')}
                className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <KeyRound size={14} />
                Change Password
              </button>
            </div>

          </form>
        </div>
      </div>
    </FormProvider>
  );
};

export default Register;
